import { forwardRef, useImperativeHandle } from "react";
import html2pdf from 'html2pdf.js';

const SupplierStatementReport = forwardRef((props, ref) => {
    const fmtMoney = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(v ?? 0);

    const downloadPDF = async (supplierDetail, isDetailed = true, startDate = null, endDate = null, selectedBanks = [], isConsolidated = false) => {
        if (!supplierDetail) return;

        const dateStr = new Date().toLocaleDateString("tr-TR");
        const filename = `${isConsolidated ? 'Konsolide_' : ''}Tedarikci_Ekstre_${supplierDetail.supplier?.name?.replace(/\s+/g, '_')}_${isDetailed ? 'Detayli' : 'Sade'}.pdf`;

        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        const dateRangeStr = (start || end)
            ? `${start ? start.toLocaleDateString("tr-TR") : "Başlangıçtan"} - ${end ? end.toLocaleDateString("tr-TR") : "Bugüne"}`
            : dateStr;

        const parseYMD = (dateStr) => {
            if (!dateStr || dateStr === "—") return null;
            const parts = dateStr.split('.');
            if (parts.length === 3) {
                return new Date(parts[2], parts[1] - 1, parts[0]);
            }
            return null;
        };

        const txs = supplierDetail.allTransactions || [];

        // Calculate carried over balance (Devreden Bakiye)
        let carriedOverBalance = 0;
        let hasDevir = false;

        if (start) {
            // Find the balance just before the first transaction within the range
            let lastTxBeforeStart = null;
            txs.forEach(t => {
                const tDate = parseYMD(t.date || t.Date);
                if (tDate && tDate < start) {
                    lastTxBeforeStart = t;
                }
            });

            if (lastTxBeforeStart) {
                carriedOverBalance = lastTxBeforeStart.balanceAfter ?? lastTxBeforeStart.balance;
                hasDevir = true;
            }
        }

        // Filter transactions within date range
        const filteredTxs = txs.filter(t => {
            const type = t.transactionType || "";
            if (type === "Açılış Bakiyesi" || type === "Devreden Bakiye") return !start; // Only show Opening/Carried Balance if no start date filter
            const tDate = parseYMD(t.date || t.Date);
            if (!tDate) return true;
            if (start && tDate < start) return false;
            if (end && tDate > end) return false;
            return true;
        });

        // Calculate summary for the filtered transactions
        let periodPurchase = 0;
        let periodPayment = 0;
        filteredTxs.forEach(t => {
            const type = t.transactionType || "";
            if (isConsolidated) {
                // consolidated transactions have debit and credit
                periodPurchase += t.credit || 0; // supplier purchases / customer payments
                periodPayment += t.debit || 0; // supplier payments / customer sales
            } else {
                if (type === "Alış") {
                    periodPurchase += Math.Abs(t.amount);
                } else if (type.includes("Ödeme") || (t.amount < 0 && type !== "İade")) {
                    periodPayment += Math.Abs(t.amount);
                }
            }
        });

        const displayPurchaseAmount = start ? periodPurchase : (isConsolidated ? filteredTxs.reduce((s, x) => s + (x.credit || 0), 0) : (supplierDetail.summary?.totalPurchaseAmount || 0));
        const displayPaidAmount = start ? periodPayment : (isConsolidated ? filteredTxs.reduce((s, x) => s + (x.debit || 0), 0) : (supplierDetail.payments || []).reduce((sum, p) => sum + Math.abs(p.amount), 0));
        const displayBalance = supplierDetail.supplier?.balance || 0;

        // Render HTML content in memory
        const element = document.createElement('div');
        element.style.padding = "0";
        element.style.fontFamily = "Arial, sans-serif";
        element.style.color = "#334155";
        element.style.fontSize = "10pt";
        element.style.lineHeight = "1.5";

        // Header
        let html = `
        <style>
            tr {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            thead {
                display: table-header-group !important;
            }
        </style>
        <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px;">
            <div>
                <h1 style="font-size: 18pt; font-weight: bold; color: #1e3a8a; margin: 0; text-transform: uppercase;">${supplierDetail.companyName || "FİRMA UNVANI"}</h1>
                <p style="font-size: 9pt; color: #64748b; margin: 5px 0 0 0;">${supplierDetail.companyAddress || ""}</p>
                <p style="font-size: 9pt; color: #64748b; margin: 2px 0 0 0;">Tel: ${supplierDetail.companyPhone || ""}</p>
            </div>
            <div style="text-align: right;">
                <h2 style="font-size: 14pt; font-weight: bold; color: #334155; margin: 0;">${isConsolidated ? 'BİRLEŞİK CARİ HESAP EKSTRESİ' : 'TEDARİKÇİ HESAP EKSTRESİ'}</h2>
                <p style="font-size: 9pt; color: #64748b; margin: 5px 0 0 0;"><strong>Tarih / Dönem:</strong> ${dateRangeStr}</p>
                <p style="font-size: 9pt; color: #64748b; margin: 2px 0 0 0;"><strong>Tip:</strong> ${isConsolidated ? 'Birleşik Döküm (Alış / Satış)' : (isDetailed ? 'Detaylı Döküm' : 'Sade Döküm')}</p>
            </div>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px;">
            <div style="flex: 1; border: 1px solid #e2e8f0; border-radius: 12px; padding: 15px; background-color: #f8fafc;">
                <h3 style="font-size: 10pt; font-weight: bold; color: #1e293b; margin: 0 0 10px 0; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; text-transform: uppercase;">Tedarikçi Bilgileri</h3>
                <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b; width: 35%;">Tedarikçi Adı:</td>
                        <td style="padding: 3px 0; color: #0f172a; font-weight: bold;">${supplierDetail.supplier?.name || "---"}</td>
                    </tr>
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b;">Telefon:</td>
                        <td style="padding: 3px 0; color: #0f172a;">${supplierDetail.supplier?.phoneNumber || "---"}</td>
                    </tr>
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b;">E-Posta:</td>
                        <td style="padding: 3px 0; color: #0f172a;">${supplierDetail.supplier?.email || "---"}</td>
                    </tr>
                    ${supplierDetail.supplier?.taxNumber ? `
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b;">Vergi No:</td>
                        <td style="padding: 3px 0; color: #0f172a;">${supplierDetail.supplier?.taxOffice || ""} ${supplierDetail.supplier?.taxNumber}</td>
                    </tr>
                    ` : ''}
                </table>
            </div>
            
            <div style="flex: 1; border: 1px solid #e2e8f0; border-radius: 12px; padding: 15px; background-color: #f8fafc;">
                <h3 style="font-size: 10pt; font-weight: bold; color: #1e293b; margin: 0 0 10px 0; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; text-transform: uppercase;">Finansal Özet</h3>
                <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                    ${isConsolidated ? `
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b; width: 50%;">Toplam Alacak (Alış):</td>
                        <td style="padding: 3px 0; color: #0f172a; font-weight: bold; text-align: right;">${fmtMoney(displayPurchaseAmount)}</td>
                    </tr>
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b;">Toplam Borç (Satış/Ödeme):</td>
                        <td style="padding: 3px 0; color: #0f172a; font-weight: bold; text-align: right;">${fmtMoney(displayPaidAmount)}</td>
                    </tr>
                    <tr style="border-top: 1px solid #cbd5e1; font-size: 10pt;">
                        <td style="padding: 6px 0 0 0; font-weight: bold; color: #1e293b;">Net Cari Bakiye:</td>
                        <td style="padding: 6px 0 0 0; color: ${displayBalance > 0 ? '#dc2626' : '#0f172a'}; font-weight: 900; text-align: right;">
                            ${fmtMoney(displayBalance)}
                            <span style="font-size: 8pt; font-weight: bold;">
                                ${displayBalance > 0 ? ' (Borçluyuz)' : (displayBalance < 0 ? ' (Alacaklıyız)' : '')}
                            </span>
                        </td>
                    </tr>
                    ` : `
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b; width: 50%;">Toplam Alış:</td>
                        <td style="padding: 3px 0; color: #0f172a; font-weight: bold; text-align: right;">${fmtMoney(displayPurchaseAmount)}</td>
                    </tr>
                    <tr>
                        <td style="padding: 3px 0; font-weight: bold; color: #64748b;">Toplam Ödeme:</td>
                        <td style="padding: 3px 0; color: #16a34a; font-weight: bold; text-align: right;">${fmtMoney(displayPaidAmount)}</td>
                    </tr>
                    <tr style="border-top: 1px solid #cbd5e1; font-size: 10pt;">
                        <td style="padding: 6px 0 0 0; font-weight: bold; color: #1e293b;">Güncel Bakiye:</td>
                        <td style="padding: 6px 0 0 0; color: ${displayBalance > 0 ? '#dc2626' : '#0f172a'}; font-weight: 900; text-align: right;">${fmtMoney(displayBalance)}</td>
                    </tr>
                    `}
                </table>
            </div>
        </div>

        <h3 style="font-size: 11pt; font-weight: bold; color: #1e293b; margin: 0 0 10px 0; text-transform: uppercase;">İşlem Dökümü</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 8.5pt;">
            <thead>
                <tr style="background-color: #f1f5f9; border-bottom: 2px solid #cbd5e1; color: #475569;">
                    <th style="padding: 8px 6px; text-align: left; font-weight: bold; width: 11%;">Tarih</th>
                    <th style="padding: 8px 6px; text-align: left; font-weight: bold; width: 13%;">İşlem Türü</th>
                    <th style="padding: 8px 6px; text-align: left; font-weight: bold; width: 13%;">Fatura No</th>
                    <th style="padding: 8px 6px; text-align: left; font-weight: bold; width: 13%;">Vade Tarihi</th>
                    ${isConsolidated ? `
                    <th style="padding: 8px 6px; text-align: right; font-weight: bold; width: 16%;">Borç (Giriş)</th>
                    <th style="padding: 8px 6px; text-align: right; font-weight: bold; width: 17%;">Alacak (Çıkış)</th>
                    ` : `
                    <th style="padding: 8px 6px; text-align: right; font-weight: bold; width: 16%;">Önceki Bakiye</th>
                    <th style="padding: 8px 6px; text-align: right; font-weight: bold; width: 17%;">İşlem Tutarı</th>
                    `}
                    <th style="padding: 8px 6px; text-align: right; font-weight: bold; width: 17%;">${isConsolidated ? 'Net Bakiye' : 'Sonraki Bakiye'}</th>
                </tr>
            </thead>
            <tbody>
        `;

        if (hasDevir) {
            html += `
            <tr style="border-bottom: 1px solid #f1f5f9; page-break-inside: avoid; break-inside: avoid; background-color: #fafafa; font-weight: bold;">
                <td style="padding: 8px 6px; color: #475569;">—</td>
                <td style="padding: 8px 6px; font-weight: bold; color: #1e293b;">Devreden Bakiye</td>
                <td style="padding: 8px 6px; color: #475569;">—</td>
                <td style="padding: 8px 6px; color: #475569;">—</td>
                ${isConsolidated ? `
                <td style="padding: 8px 6px; text-align: right; color: #475569;">—</td>
                <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: #0f172a;">—</td>
                ` : `
                <td style="padding: 8px 6px; text-align: right; color: #475569;">—</td>
                <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: #0f172a;">—</td>
                `}
                <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: #0f172a;">${fmtMoney(carriedOverBalance)}</td>
            </tr>
            `;
        }

        if (filteredTxs.length === 0 && !hasDevir) {
            html += `
                <tr>
                    <td colspan="${isConsolidated ? 7 : 7}" style="padding: 15px; text-align: center; color: #94a3b8; font-style: italic;">Herhangi bir işlem kaydı bulunamadı.</td>
                </tr>
            `;
        } else {
            filteredTxs.forEach((t) => {
                const isOpening = t.transactionType === "Açılış Bakiyesi";
                const isDevreden = t.transactionType === "Devreden Bakiye";

                let debitText = "—";
                let creditText = "—";
                let prevBalanceText = "—";
                let amountText = "—";
                let amountColor = "#0f172a";

                if (isConsolidated) {
                    debitText = isDevreden ? "—" : (t.debit > 0 ? fmtMoney(t.debit) : "—");
                    creditText = isDevreden ? "—" : (t.credit > 0 ? fmtMoney(t.credit) : "—");
                } else {
                    prevBalanceText = isOpening || isDevreden ? "—" : fmtMoney(t.balanceBefore);
                    amountText = isDevreden ? "—" : ((t.amount > 0 ? "+" : "") + fmtMoney(t.amount));
                    amountColor = t.amount < 0 ? "#dc2626" : "#0f172a";
                }

                let dueDateText = t.dueDate || "—";

                html += `
                <tr style="border-bottom: 1px solid #f1f5f9; page-break-inside: avoid; break-inside: avoid; ${isDevreden ? 'background-color: #fafafa; font-weight: bold;' : ''}">
                    <td style="padding: 8px 6px; color: #475569;">${t.date}</td>
                    <td style="padding: 8px 6px; font-weight: bold; color: #1e293b;">${t.transactionType}</td>
                    <td style="padding: 8px 6px; color: #475569;">${t.invoiceNo || "—"}</td>
                    <td style="padding: 8px 6px; color: #475569;">${dueDateText}</td>
                    ${isConsolidated ? `
                    <td style="padding: 8px 6px; text-align: right; color: #0f172a;">${debitText}</td>
                    <td style="padding: 8px 6px; text-align: right; color: #dc2626;">${creditText}</td>
                    ` : `
                    <td style="padding: 8px 6px; text-align: right; color: #475569;">${prevBalanceText}</td>
                    <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: ${isDevreden ? '#0f172a' : amountColor};">${amountText}</td>
                    `}
                    <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: #0f172a;">${fmtMoney(t.balanceAfter ?? t.balance)}</td>
                </tr>
                `;

                if (isDetailed) {
                    if ((t.referenceType === "PURCHASE" || t.transactionType === "Alış") && t.referenceId) {
                        const purchaseProducts = (supplierDetail.purchaseHistory || []).filter(p => p.transactionId === t.referenceId);
                        if (purchaseProducts.length > 0) {
                            html += `
                            <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; page-break-inside: avoid; break-inside: avoid;">
                                <td style="padding: 4px 6px;"></td>
                                <td colspan="6" style="padding: 6px 12px; border-left: 2px solid #3b82f6;">
                                    <table style="width: 100%; border-collapse: collapse; font-size: 7.5pt;">
                                        <tbody>
                                            ${purchaseProducts.map(p => `
                                                <tr style="color: #334155; page-break-inside: avoid; break-inside: avoid;">
                                                    <td style="padding: 2px 0; text-align: left; font-weight: 500;">• ${p.productName}</td>
                                                    <td style="padding: 2px 0; text-align: right; color: #64748b; width: 20%;">${p.quantity} Adet</td>
                                                    <td style="padding: 2px 0; text-align: right; color: #64748b; width: 20%;">x ${fmtMoney(p.unitPrice)}</td>
                                                    <td style="padding: 2px 0; text-align: right; font-weight: 600; width: 25%; color: #0f172a;">${fmtMoney(p.totalPrice)}</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            `;
                        }
                    } else if ((t.referenceType === "SALE" || t.transactionType === "Satış") && t.referenceId) {
                        const saleProducts = (supplierDetail.soldProducts || []).filter(p => p.saleId === t.referenceId);
                        if (saleProducts.length > 0) {
                            html += `
                            <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; page-break-inside: avoid; break-inside: avoid;">
                                <td style="padding: 4px 6px;"></td>
                                <td colspan="6" style="padding: 6px 12px; border-left: 2px solid #10b981;">
                                    <table style="width: 100%; border-collapse: collapse; font-size: 7.5pt;">
                                        <tbody>
                                            ${saleProducts.map(p => `
                                                <tr style="color: #334155; page-break-inside: avoid; break-inside: avoid;">
                                                    <td style="padding: 2px 0; text-align: left; font-weight: 500;">• ${p.productName}</td>
                                                    <td style="padding: 2px 0; text-align: right; color: #64748b; width: 20%;">${p.quantity} Adet</td>
                                                    <td style="padding: 2px 0; text-align: right; color: #64748b; width: 20%;">x ${fmtMoney(p.unitPrice)}</td>
                                                    <td style="padding: 2px 0; text-align: right; font-weight: 600; width: 25%; color: #0f172a;">${fmtMoney(p.totalAmount)}</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            `;
                        }
                    }
                }
            });
        }

        html += `
            </tbody>
        </table>
        `;

        if (selectedBanks && selectedBanks.length > 0) {
            html += `
            <div style="margin-top: 30px; border-top: 1px solid #cbd5e1; padding-top: 15px; page-break-inside: avoid; break-inside: avoid;">
                <h4 style="font-size: 9.5pt; font-weight: bold; color: #1e293b; margin: 0 0 8px 0; text-transform: uppercase;">Banka Hesap Bilgilerimiz</h4>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; font-size: 8pt; color: #475569;">
                    ${selectedBanks.map(b => `
                        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px;">
                            <div style="font-weight: bold; color: #1e293b;">${b.name}</div>
                            <div style="font-family: monospace; font-size: 7.5pt; margin-top: 4px;">IBAN: ${b.accountNumber || "—"}</div>
                            <div style="color: #64748b; font-size: 7pt; margin-top: 2px;">Para Birimi: ${b.currencyCode || "TRY"}</div>
                        </div>
                    `).join('')}
                </div>
            </div>
            `;
        }

        // Footer note
        html += `
        <div style="margin-top: 50px; border-top: 1px solid #e2e8f0; padding-top: 10px; text-align: center; font-size: 8pt; color: #94a3b8;">
            Bu belge kasambenim.com üzerinden otomatik oluşturulmuştur. Mutabakat amaçlıdır.
        </div>
        `;

        element.innerHTML = html;

        const opt = {
            margin: 10,
            filename: filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();
    };

    useImperativeHandle(ref, () => ({
        downloadPDF
    }));

    return null;
});

export default SupplierStatementReport;
